import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel from '../../../../../commonDesign/SydLabel'
import { provisionedTargets } from '../../../../../../api/integrations'
import { decipherError } from '../../../../../../utils/decipherError'

function AssignTargetForm ({ job, onCancel }) {
  const [selectedTargetId, setSelectedTargetId] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')

  const { data: targetsData, isLoading } = provisionedTargets.useListProvisionedTargetTables()
  const { mutateAsync: assignJob } = provisionedTargets.useAssignJobToProvisionedTarget()

  const targets = useMemo(() => targetsData?.result || [], [targetsData])

  const filteredTargets = useMemo(() => {
    if (!search.trim()) return targets
    const term = search.toLowerCase()
    return targets.filter(t =>
      t.tableName?.toLowerCase().includes(term) ||
      t.domain?.toLowerCase().includes(term) ||
      t.description?.toLowerCase().includes(term)
    )
  }, [targets, search])

  const handleAssign = useCallback(async () => {
    if (!selectedTargetId || !job) return
    try {
      setError(null)
      setProcessing(true)
      await assignJob({
        jobConfigurationId: job.jobConfigurationId,
        provisionedTargetTableId: selectedTargetId
      })
      onCancel?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [selectedTargetId, job, assignJob, onCancel])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SydLabel label='Search Targets'>
          <SydInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Filter by table name, domain, description...'
            minWidth='0'
          />
        </SydLabel>
      </Grid>
      <Grid item xs={12}>
        {isLoading ? (
          <div>Loading targets...</div>
        ) : filteredTargets.length === 0 ? (
          <div style={{ padding: '1rem 0', opacity: 0.6 }}>
            {targets.length === 0
              ? 'No provisioned targets available'
              : 'No targets match your search'}
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table className='opal-table'>
              <thead>
                <tr>
                  <th />
                  <th>Table Name</th>
                  <th>Domain</th>
                  <th>Description</th>
                  <th>Locked</th>
                </tr>
              </thead>
              <tbody>
                {filteredTargets.map(t => (
                  <tr
                    key={t.provisionedTargetTableId}
                    onClick={() => setSelectedTargetId(t.provisionedTargetTableId)}
                    style={{ cursor: 'pointer', background: selectedTargetId === t.provisionedTargetTableId ? 'var(--color-primary-light, rgba(0,102,255,0.08))' : undefined }}
                  >
                    <td>
                      <input
                        type='radio'
                        checked={selectedTargetId === t.provisionedTargetTableId}
                        onChange={() => setSelectedTargetId(t.provisionedTargetTableId)}
                      />
                    </td>
                    <td>{t.tableName}</td>
                    <td>{t.domain || '\u2014'}</td>
                    <td>{t.description || '\u2014'}</td>
                    <td>{t.isLocked ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            processing={processing}
            disabled={!selectedTargetId}
            variant='primary'
            size='md'
            onClick={handleAssign}
          >
            Assign
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

AssignTargetForm.propTypes = {
  job: PropTypes.object,
  onCancel: PropTypes.func
}

export default AssignTargetForm
